import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, forkJoin, map, of, switchMap, throwError, timeout } from 'rxjs';
import { API_BASE_URL } from '../core/api-base-url';

export interface MaintenanceHistoryEntry {
  monthLabel: string;
  mechanic: string;
  status: string;
}

export interface MaintenancePart {
  name: string;
  durabilityPercent: number;
  previousServiceLabel: string;
  cost: number | null;
  status: string;
  history: MaintenanceHistoryEntry[];
}

export interface MechanicOption {
  name: string;
  contact: string;
  location: string;
}

export interface MaintenanceReservation {
  id: number;
  ownerId: string;
  vehicleId?: number;
  partName: string;
  mechanic: string;
  date: string;
  fromTime: string;
  toTime: string;
  status: 'Pending Approval' | 'Approved' | 'Denied';
  createdAtUtc: string;
}

interface MaintenancePartDefinition {
  name: string;
  serviceType: string;
  intervalKm: number;
  dashboardAliases: string[];
  appointmentTitles: string[];
}

interface AppointmentDto {
  id: number;
  ownerId: string;
  vehicleProfileId?: number | null;
  title: string;
  notes?: string | null;
  requestedStartUtc: string;
  requestedEndUtc: string;
  confirmedStartUtc?: string | null;
  confirmedEndUtc?: string | null;
  status: string;
  mechanicNotes?: string | null;
}

interface AppointmentMeta {
  mechanic: string;
  fromTime: string;
  toTime: string;
}

interface CreateAppointmentRequest {
  ownerId: string;
  vehicleProfileId: number | null;
  title: string;
  notes: string;
  requestedStartUtc: string;
  requestedEndUtc: string;
}

interface ConfirmAppointmentRequest {
  confirmedStartUtc: string;
  confirmedEndUtc: string;
  mechanicNotes?: string;
}

interface RejectAppointmentRequest {
  mechanicNotes?: string;
}

interface MechanicDirectoryEntryDto {
  username: string;
  name: string;
  contact: string;
  location: string;
}

interface ServiceRecordDto {
  id: number;
  vehicleProfileId: number;
  serviceType: string;
  odometerKm: number;
  serviceDateUtc: string;
  totalCost: number;
}

interface VehicleProfileDto {
  id: number;
  ownerId: string;
  make: string;
  model: string;
  year: number;
  currentOdometerKm: number;
}

interface VehiclePartStateDto {
  id: number;
  vehicleProfileId: number;
  name: string;
  capacityKm: number;
  lastServiceOdometerKm: number;
  lastServiceUtc?: string | null;
  remainingLifePercent: number;
}

@Injectable({ providedIn: 'root' })
export class MaintenanceSchedulingService {
  private readonly requestTimeoutMs = 12000;
  private readonly authApiBaseUrl = `${API_BASE_URL}/api/auth`;
  private readonly appointmentsApiBaseUrl = `${API_BASE_URL}/api/appointments`;
  private readonly serviceRecordsApiBaseUrl = `${API_BASE_URL}/api/servicerecords`;
  private readonly vehiclePartStatesApiBaseUrl = `${API_BASE_URL}/api/vehiclepartstates`;
  // Prefix used to store structured appointment metadata in a single notes field.
  private readonly notesPrefix = 'AUTOINSIGHT_META:';

  // Canonical definitions used to normalize service records, appointments, and part-state rows.
  private readonly partDefinitions: MaintenancePartDefinition[] = [
    {
      name: 'Oil Filter',
      serviceType: 'Engine Oil',
      intervalKm: 12000,
      dashboardAliases: ['oil filter', 'engine oil'],
      appointmentTitles: ['oil filter']
    },
    {
      name: 'Engine Oil',
      serviceType: 'Engine Oil',
      intervalKm: 12000,
      dashboardAliases: ['engine oil'],
      appointmentTitles: ['engine oil']
    },
    {
      name: 'Spark Plugs',
      serviceType: 'Spark Plugs',
      intervalKm: 100000,
      dashboardAliases: ['spark plugs', 'spark plug'],
      appointmentTitles: ['spark plugs']
    },
    {
      name: 'Brake Pads',
      serviceType: 'Brake Pads',
      intervalKm: 55000,
      dashboardAliases: ['brake pads', 'brake pad'],
      appointmentTitles: ['brake pads']
    },
    {
      name: 'Cabin Air Filter',
      serviceType: 'Cabin Air Filter',
      intervalKm: 20000,
      dashboardAliases: ['cabin air filter'],
      appointmentTitles: ['cabin air filter']
    },
    {
      name: 'Engine Air Filter',
      serviceType: 'Engine Air Filter',
      intervalKm: 25000,
      dashboardAliases: ['engine air filter'],
      appointmentTitles: ['engine air filter']
    },
    {
      name: 'Fuel Filter',
      serviceType: 'Fuel Filter',
      intervalKm: 50000,
      dashboardAliases: ['fuel filter'],
      appointmentTitles: ['fuel filter']
    }
  ];

  private readonly presetPartsSnapshot: MaintenancePart[] = [
    {
      name: 'Oil Filter',
      durabilityPercent: 50,
      previousServiceLabel: 'February 2025',
      cost: null,
      status: '',
      history: []
    },
    {
      name: 'Engine Oil',
      durabilityPercent: 50,
      previousServiceLabel: 'March 2025',
      cost: null,
      status: '',
      history: []
    },
    {
      name: 'Spark Plugs',
      durabilityPercent: 70,
      previousServiceLabel: 'January 2025',
      cost: null,
      status: '',
      history: []
    },
    {
      name: 'Brake Pads',
      durabilityPercent: 80,
      previousServiceLabel: 'December 2025',
      cost: null,
      status: '',
      history: []
    },
    {
      name: 'Cabin Air Filter',
      durabilityPercent: 60,
      previousServiceLabel: 'March 2025',
      cost: null,
      status: 'Scheduled',
      history: []
    },
    {
      name: 'Engine Air Filter',
      durabilityPercent: 80,
      previousServiceLabel: 'June 2025',
      cost: null,
      status: '',
      history: []
    },
    {
      name: 'Fuel Filter',
      durabilityPercent: 80,
      previousServiceLabel: 'June 2025',
      cost: null,
      status: 'Scheduled',
      history: []
    }
  ];

  private readonly presetPartDetails = new Map<string, MaintenancePart>([
    [
      'engine oil',
      {
        name: 'Engine Oil',
        durabilityPercent: 45,
        previousServiceLabel: 'March 2025',
        cost: 100,
        status: '',
        history: [
          { monthLabel: 'March 2026', mechanic: 'Peter Griffin', status: 'Scheduled' },
          { monthLabel: 'April 2024', mechanic: 'Spongebob', status: '' },
          { monthLabel: 'June 2023', mechanic: 'Spongebob', status: '' },
          { monthLabel: 'August 2022', mechanic: 'Peter Griffin', status: '' },
          { monthLabel: 'October 2021', mechanic: 'Peter Griffin', status: '' }
        ]
      }
    ]
  ]);

  private readonly presetMechanicsSnapshot: MechanicOption[] = [
    { name: 'Peter Griffin', contact: '(306) 123-4567', location: 'East Regina' },
    { name: 'Stuart Little', contact: '(306) 234-4567', location: 'West Regina' },
    { name: 'Michael Scott', contact: '(306) 123-6789', location: 'North Regina' },
    { name: 'Percy Jackson', contact: '(306) 334-4567', location: 'South Regina' },
    { name: 'Choji Akimichi', contact: '(306) 123-2345', location: 'East Regina' },
    { name: 'Bart Simpson', contact: '(306) 123-3456', location: 'East Regina' },
    { name: 'Tarzan', contact: '(306) 123-9876', location: 'West Regina' },
    { name: 'Tom', contact: '(306) 321-4567', location: 'South Regina' },
    { name: 'Jerry', contact: '(306) 456-4567', location: 'East Regina' },
    { name: 'Homer', contact: '(306) 345-5678', location: 'North Regina' }
  ];

  constructor(private readonly http: HttpClient) {}

  getFallbackPartsSnapshot(): MaintenancePart[] {
    return this.cloneParts(this.presetPartsSnapshot);
  }

  getFallbackMechanicsSnapshot(): MechanicOption[] {
    return this.presetMechanicsSnapshot.map((mechanic) => ({ ...mechanic }));
  }

  getPresetPartDetail(partName: string): MaintenancePart | null {
    const normalizedPartName = this.normalizePartName(partName);
    const detailedPreset = this.presetPartDetails.get(normalizedPartName);
    if (detailedPreset) {
      return this.clonePart(detailedPreset);
    }

    const fallback = this.presetPartsSnapshot.find(
      (part) => this.normalizePartName(part.name) === normalizedPartName
    );
    return fallback ? this.clonePart(fallback) : null;
  }

  getParts(ownerId?: string, vehicleId?: number): Observable<MaintenancePart[]> {
    const normalizedOwner = this.normalizeOwnerId(ownerId);
    if (vehicleId === undefined) {
      return of(this.getFallbackPartsSnapshot());
    }

    // Build maintenance cards from three sources in one pass.
    return forkJoin({
      appointments: normalizedOwner
        ? this.getAppointmentsByOwner(normalizedOwner).pipe(catchError(() => of([] as AppointmentDto[])))
        : of([] as AppointmentDto[]),
      partStates: this.getVehiclePartStates(vehicleId),
      serviceRecords: this.getServiceRecordsByVehicle(vehicleId).pipe(catchError(() => of([] as ServiceRecordDto[])))
    }).pipe(
      map(({ appointments, partStates, serviceRecords }) =>
        this.buildPartViews(appointments, vehicleId, serviceRecords, partStates)
      )
    );
  }

  getDashboardSeedParts(ownerId?: string, vehicleId?: number, refreshToken?: number): Observable<MaintenancePart[]> {
    if (vehicleId === undefined) {
      return of([]);
    }

    // Seed dashboard maintenance quickly from part-state endpoint only.
    return this.getVehiclePartStates(vehicleId, refreshToken).pipe(
      map((partStates) => (partStates.length > 0 ? this.buildPartViews([], vehicleId, [], partStates) : [])),
      catchError(() => of([] as MaintenancePart[]))
    );
  }

  getPart(partName: string, ownerId?: string, vehicleId?: number): Observable<MaintenancePart | null> {
    const normalizedPartName = partName.trim().toLowerCase();
    if (vehicleId === undefined) {
      return of(this.getPresetPartDetail(partName));
    }

    return this.getParts(ownerId, vehicleId).pipe(
      map((parts) =>
        parts.find((item) => item.name.trim().toLowerCase() === normalizedPartName) ??
        this.getPresetPartDetail(partName)
      )
    );
  }

  getMechanics(): Observable<MechanicOption[]> {
    return this.http.get<MechanicDirectoryEntryDto[]>(`${this.authApiBaseUrl}/mechanics`).pipe(
      map((rows) =>
        rows.map((row) => ({
          name: row.name,
          contact: row.contact,
          location: row.location
        }))
      ),
      map((mechanics) => (mechanics.length > 0 ? mechanics : this.getFallbackMechanicsSnapshot())),
      catchError(() => of(this.getFallbackMechanicsSnapshot()))
    );
  }

  getScheduledMechanic(partName: string, ownerId?: string, vehicleId?: number): Observable<string | null> {
    const normalizedOwner = this.normalizeOwnerId(ownerId);
    if (!normalizedOwner) {
      return of(this.resolvePresetMechanic(partName));
    }

    return this.getAppointmentsByOwner(normalizedOwner).pipe(
      catchError(() => of([] as AppointmentDto[])),
      map((appointments) => {
        const scoped = this.appointmentsForVehicle(appointments, vehicleId);
        const latest = this.latestAppointmentForPart(scoped, partName);
        const metadata = this.parseAppointmentMeta(latest?.notes);
        return metadata?.mechanic ?? this.resolvePresetMechanic(partName);
      })
    );
  }

  scheduleMaintenance(
    ownerId: string,
    vehicleId: number | undefined,
    partName: string,
    mechanicName: string,
    date: string,
    fromTime: string,
    toTime: string
  ): Observable<void> {
    const normalizedOwner = this.normalizeOwnerId(ownerId);
    const trimmedPart = partName.trim();
    const trimmedMechanic = mechanicName.trim();

    if (!normalizedOwner) {
      return throwError(() => new Error('Owner is required.'));
    }

    if (!trimmedPart) {
      return throwError(() => new Error('Part name is required.'));
    }

    if (!trimmedMechanic) {
      return throwError(() => new Error('Mechanic is required.'));
    }

    const slot = this.toUtcRange(date, fromTime, toTime);
    if (!slot) {
      return throwError(() => new Error('Invalid appointment date/time.'));
    }

    const payload: CreateAppointmentRequest = {
      ownerId: normalizedOwner,
      vehicleProfileId: vehicleId ?? null,
      title: trimmedPart,
      notes: this.serializeAppointmentMeta({
        mechanic: trimmedMechanic,
        fromTime,
        toTime
      }),
      requestedStartUtc: slot.startUtc,
      requestedEndUtc: slot.endUtc
    };

    return this.http.post<AppointmentDto>(this.appointmentsApiBaseUrl, payload).pipe(map(() => void 0));
  }

  getReservationsForMechanic(mechanicName?: string): Observable<MaintenanceReservation[]> {
    const normalizedMechanic = mechanicName?.trim().toLowerCase() ?? '';

    return this.getAllAppointments().pipe(
      map((appointments) =>
        appointments
          .map((appointment) => this.toReservationView(appointment))
          .filter((reservation): reservation is MaintenanceReservation => reservation !== null)
          .filter((reservation) =>
            normalizedMechanic ? reservation.mechanic.trim().toLowerCase() === normalizedMechanic : true
          )
          .sort((a, b) => (a.createdAtUtc < b.createdAtUtc ? 1 : -1))
      )
    );
  }

  updateReservationStatus(reservationId: number, approved: boolean): Observable<void> {
    return this.getAllAppointments().pipe(
      map((appointments) => appointments.find((row) => row.id === reservationId) ?? null),
      switchMap((appointment) => {
        if (!appointment) {
          return throwError(() => new Error('Reservation not found.'));
        }

        if (approved) {
          const payload: ConfirmAppointmentRequest = {
            confirmedStartUtc: appointment.requestedStartUtc,
            confirmedEndUtc: appointment.requestedEndUtc,
            mechanicNotes: 'Approved by mechanic'
          };

          return this.http
            .put<AppointmentDto>(`${this.appointmentsApiBaseUrl}/${reservationId}/confirm`, payload)
            .pipe(map(() => void 0));
        }

        const payload: RejectAppointmentRequest = {
          mechanicNotes: 'Denied by mechanic'
        };

        return this.http
          .put<AppointmentDto>(`${this.appointmentsApiBaseUrl}/${reservationId}/reject`, payload)
          .pipe(map(() => void 0));
      })
    );
  }

  private getAllAppointments(): Observable<AppointmentDto[]> {
    return this.http.get<AppointmentDto[]>(this.appointmentsApiBaseUrl).pipe(timeout(this.requestTimeoutMs));
  }

  private getAppointmentsByOwner(ownerId: string): Observable<AppointmentDto[]> {
    return this.http
      .get<AppointmentDto[]>(`${this.appointmentsApiBaseUrl}/owner/${encodeURIComponent(ownerId)}`)
      .pipe(timeout(this.requestTimeoutMs));
  }

  private getServiceRecordsByVehicle(vehicleId?: number): Observable<ServiceRecordDto[]> {
    if (vehicleId === undefined) {
      return of([] as ServiceRecordDto[]);
    }

    return this.http
      .get<ServiceRecordDto[]>(`${this.serviceRecordsApiBaseUrl}/vehicle/${vehicleId}`)
      .pipe(timeout(this.requestTimeoutMs));
  }

  private appointmentsForVehicle(appointments: AppointmentDto[], vehicleId?: number): AppointmentDto[] {
    if (vehicleId === undefined) {
      return appointments;
    }

    return appointments.filter((appointment) => (appointment.vehicleProfileId ?? undefined) === vehicleId);
  }

  private buildPartViews(
    appointments: AppointmentDto[],
    vehicleId: number | undefined,
    serviceRecords: ServiceRecordDto[],
    partStates: VehiclePartStateDto[]
  ): MaintenancePart[] {
    const scopedAppointments = this.appointmentsForVehicle(appointments, vehicleId);
    return this.partDefinitions.map((definition) =>
      this.toPartView(definition, scopedAppointments, serviceRecords, this.findPartState(partStates, definition))
    );
  }

  private toPartView(
    definition: MaintenancePartDefinition,
    appointments: AppointmentDto[],
    serviceRecords: ServiceRecordDto[],
    partState: VehiclePartStateDto | null
  ): MaintenancePart {
    const matchedServiceRecords = this.matchingServiceRecords(serviceRecords, definition);
    const latestServiceRecord = matchedServiceRecords[0] ?? null;
    const latestAppointment = this.latestAppointmentForDefinition(appointments, definition);

    return {
      name: definition.name,
      durabilityPercent: this.resolveDurabilityPercent(
        partState
      ),
      previousServiceLabel:
        partState?.lastServiceUtc
          ? this.formatMonthYear(partState.lastServiceUtc)
          : (latestServiceRecord ? this.formatMonthYear(latestServiceRecord.serviceDateUtc) : 'Not recorded'),
      cost:
        latestServiceRecord && Number.isFinite(latestServiceRecord.totalCost) && latestServiceRecord.totalCost > 0
          ? latestServiceRecord.totalCost
          : null,
      status: this.resolvePartStatus(latestAppointment, latestServiceRecord),
      history: this.buildHistory(definition, matchedServiceRecords, appointments)
    };
  }

  private resolveDurabilityPercent(partState: VehiclePartStateDto | null): number {
    if (!partState) {
      return 0;
    }

    return Math.max(0, Math.min(100, Math.round(partState.remainingLifePercent)));
  }

  private toReservationView(appointment: AppointmentDto): MaintenanceReservation | null {
    const metadata = this.parseAppointmentMeta(appointment.notes);
    if (!metadata) {
      return null;
    }

    return {
      id: appointment.id,
      ownerId: appointment.ownerId,
      vehicleId: appointment.vehicleProfileId ?? undefined,
      partName: appointment.title,
      mechanic: metadata.mechanic,
      date: this.formatDate(appointment.requestedStartUtc),
      fromTime: metadata.fromTime || this.formatTime(appointment.requestedStartUtc),
      toTime: metadata.toTime || this.formatTime(appointment.requestedEndUtc),
      status: this.mapReservationStatus(appointment.status),
      createdAtUtc: appointment.requestedStartUtc
    };
  }

  private resolvePartStatus(appointment: AppointmentDto | null, latestServiceRecord: ServiceRecordDto | null): string {
    if (appointment) {
      return this.mapPartStatus(appointment.status);
    }

    return latestServiceRecord ? 'Complete' : '';
  }

  private latestAppointmentForPart(appointments: AppointmentDto[], partName: string): AppointmentDto | null {
    const normalized = partName.trim().toLowerCase();
    const matching = appointments.filter((row) => row.title.trim().toLowerCase() === normalized);
    if (matching.length === 0) {
      return null;
    }

    return matching.reduce((latest, current) =>
      latest.requestedStartUtc >= current.requestedStartUtc ? latest : current
    );
  }

  private latestAppointmentForDefinition(
    appointments: AppointmentDto[],
    definition: MaintenancePartDefinition
  ): AppointmentDto | null {
    const matching = appointments.filter((row) => this.matchesAppointmentToDefinition(row.title, definition));
    if (matching.length === 0) {
      return null;
    }

    return matching.reduce((latest, current) =>
      latest.requestedStartUtc >= current.requestedStartUtc ? latest : current
    );
  }

  private normalizeOwnerId(ownerId?: string): string {
    const trimmed = ownerId?.trim() ?? '';
    return trimmed;
  }

  private matchingServiceRecords(
    serviceRecords: ServiceRecordDto[],
    definition: MaintenancePartDefinition
  ): ServiceRecordDto[] {
    return serviceRecords
      .filter((record) => this.normalizePartName(record.serviceType) === this.normalizePartName(definition.serviceType))
      .sort((a, b) => (a.serviceDateUtc < b.serviceDateUtc ? 1 : -1));
  }

  private matchesAppointmentToDefinition(title: string, definition: MaintenancePartDefinition): boolean {
    const normalizedTitle = this.normalizePartName(title);
    return definition.appointmentTitles.some(
      (appointmentTitle) => normalizedTitle === this.normalizePartName(appointmentTitle)
    );
  }

  private buildHistory(
    definition: MaintenancePartDefinition,
    serviceRecords: ServiceRecordDto[],
    appointments: AppointmentDto[]
  ): MaintenanceHistoryEntry[] {
    const appointmentEntries = appointments
      .filter((appointment) => this.matchesAppointmentToDefinition(appointment.title, definition))
      .map((appointment) => {
        const metadata = this.parseAppointmentMeta(appointment.notes);
        return {
          sortUtc: appointment.requestedStartUtc,
          entry: {
            monthLabel: this.formatMonthYear(appointment.requestedStartUtc),
            mechanic: metadata?.mechanic ?? 'Mechanic pending',
            status: this.mapPartStatus(appointment.status)
          }
        };
      });

    const serviceEntries = serviceRecords.map((record) => ({
      sortUtc: record.serviceDateUtc,
      entry: {
        monthLabel: this.formatMonthYear(record.serviceDateUtc),
        mechanic: 'Recorded service',
        status: 'Complete'
      }
    }));

    return [...appointmentEntries, ...serviceEntries]
      .sort((a, b) => (a.sortUtc < b.sortUtc ? 1 : -1))
      .map((item) => item.entry)
      .slice(0, 5);
  }

  private getVehiclePartStates(vehicleId?: number, refreshToken?: number): Observable<VehiclePartStateDto[]> {
    if (vehicleId === undefined) {
      return of([] as VehiclePartStateDto[]);
    }

    // Timestamp query param helps bypass stale intermediary caches after mileage/maintenance writes.
    const url =
      refreshToken !== undefined
        ? `${this.vehiclePartStatesApiBaseUrl}/vehicle/${vehicleId}?_ts=${refreshToken}`
        : `${this.vehiclePartStatesApiBaseUrl}/vehicle/${vehicleId}`;

    return this.http
      .get<VehiclePartStateDto[]>(url)
      .pipe(timeout(this.requestTimeoutMs));
  }

  private findPartState(
    partStates: VehiclePartStateDto[],
    definition: MaintenancePartDefinition
  ): VehiclePartStateDto | null {
    const normalizedCandidates = [definition.name, ...definition.dashboardAliases].map((value) =>
      this.normalizePartName(value)
    );

    return partStates.find((state) => normalizedCandidates.includes(this.normalizePartName(state.name))) ?? null;
  }

  private normalizePartName(value: string): string {
    return value.trim().toLowerCase().replace(/\s+/g, ' ');
  }

  private formatMonthYear(utcIso: string): string {
    const parsed = new Date(utcIso);
    if (!Number.isFinite(parsed.getTime())) {
      return 'Not recorded';
    }

    return parsed.toLocaleString('en-US', {
      month: 'long',
      year: 'numeric'
    });
  }

  private mapReservationStatus(status: string): 'Pending Approval' | 'Approved' | 'Denied' {
    const normalized = status.trim().toLowerCase();
    if (normalized === 'confirmed') {
      return 'Approved';
    }

    if (normalized === 'rejected') {
      return 'Denied';
    }

    return 'Pending Approval';
  }

  private mapPartStatus(status: string): string {
    const normalized = status.trim().toLowerCase();
    if (normalized === 'confirmed') {
      return 'Scheduled';
    }

    if (normalized === 'rejected') {
      return 'Denied';
    }

    return 'Pending Approval';
  }

  private serializeAppointmentMeta(meta: AppointmentMeta): string {
    return `${this.notesPrefix}${JSON.stringify(meta)}`;
  }

  private parseAppointmentMeta(notes: string | null | undefined): AppointmentMeta | null {
    if (!notes || !notes.startsWith(this.notesPrefix)) {
      return null;
    }

    const payload = notes.slice(this.notesPrefix.length);
    try {
      const parsed = JSON.parse(payload);
      if (!parsed || typeof parsed !== 'object') {
        return null;
      }

      const mechanic = typeof parsed['mechanic'] === 'string' ? parsed['mechanic'].trim() : '';
      const fromTime = typeof parsed['fromTime'] === 'string' ? parsed['fromTime'].trim() : '';
      const toTime = typeof parsed['toTime'] === 'string' ? parsed['toTime'].trim() : '';

      if (!mechanic) {
        return null;
      }

      return {
        mechanic,
        fromTime,
        toTime
      };
    } catch {
      return null;
    }
  }

  private toUtcRange(date: string, fromTime: string, toTime: string): { startUtc: string; endUtc: string } | null {
    const dateParts = date.trim().split('-');
    if (dateParts.length !== 3) {
      return null;
    }

    const year = Number.parseInt(dateParts[0], 10);
    const month = Number.parseInt(dateParts[1], 10);
    const day = Number.parseInt(dateParts[2], 10);
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
      return null;
    }

    const parsedFrom = this.parseTimeLabel(fromTime);
    const parsedTo = this.parseTimeLabel(toTime);
    if (!parsedFrom || !parsedTo) {
      return null;
    }

    const startLocal = new Date(year, month - 1, day, parsedFrom.hour24, parsedFrom.minute, 0, 0);
    const endLocal = new Date(year, month - 1, day, parsedTo.hour24, parsedTo.minute, 0, 0);
    if (!Number.isFinite(startLocal.getTime()) || !Number.isFinite(endLocal.getTime())) {
      return null;
    }

    if (endLocal <= startLocal) {
      return null;
    }

    return {
      startUtc: startLocal.toISOString(),
      endUtc: endLocal.toISOString()
    };
  }

  private parseTimeLabel(label: string): { hour24: number; minute: number } | null {
    const match = label.trim().match(/^(\d{1,2}):(\d{2})(?:\s*(AM|PM))?$/i);
    if (!match) {
      return null;
    }

    const hour = Number.parseInt(match[1], 10);
    const minute = Number.parseInt(match[2], 10);
    const period = match[3]?.toUpperCase();

    if (!Number.isFinite(hour) || !Number.isFinite(minute) || minute < 0 || minute > 59) {
      return null;
    }

    if (!period) {
      if (hour < 0 || hour > 23) {
        return null;
      }
      return { hour24: hour, minute };
    }

    if (hour < 1 || hour > 12) {
      if (hour < 0 || hour > 23) {
        return null;
      }
      return { hour24: hour, minute };
    }

    let hour24 = hour % 12;
    if (period === 'PM') {
      hour24 += 12;
    }
    return { hour24, minute };
  }

  private formatDate(utcIso: string): string {
    const parsed = new Date(utcIso);
    if (!Number.isFinite(parsed.getTime())) {
      return '';
    }

    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private formatTime(utcIso: string): string {
    const parsed = new Date(utcIso);
    if (!Number.isFinite(parsed.getTime())) {
      return '';
    }

    const minute = String(parsed.getMinutes()).padStart(2, '0');
    const hour24 = parsed.getHours();
    const period = hour24 >= 12 ? 'PM' : 'AM';
    const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
    return `${String(hour12).padStart(2, '0')}:${minute} ${period}`;
  }

  private resolvePresetMechanic(partName: string): string | null {
    const presetPart = this.getPresetPartDetail(partName);
    if (!presetPart) {
      return null;
    }

    return presetPart.history.find((entry) => entry.mechanic.trim().length > 0)?.mechanic ?? null;
  }

  private cloneParts(parts: MaintenancePart[]): MaintenancePart[] {
    return parts.map((part) => this.clonePart(part));
  }

  private clonePart(part: MaintenancePart): MaintenancePart {
    return {
      ...part,
      history: part.history.map((entry) => ({ ...entry }))
    };
  }
}
